import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Observable, Subject, debounceTime, distinctUntilChanged, forkJoin, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { MarketingFormsService } from '../service/marketing-forms.service';
import { extractApiErrorMessage } from '../utils/api-error.util';
import { FormEditorDrawerService } from '../service/form-editor-drawer.service';
import { TemplateService } from '../service/template.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';

import { MetricsBarComponent } from '../components/metrics-bar/metrics-bar.component';
import { FormsTableComponent, LifecycleAction } from '../components/forms-table/forms-table.component';
import { FormModalComponent } from '../components/form-modal/form-modal.component';
import { FormDetailModalComponent } from '../components/form-detail-modal/form-detail-modal.component';
import { AnalyticsDashboardComponent } from '../components/analytics-dashboard/analytics-dashboard.component';

import { FormListModel, FormStatus, FormCreatePayload } from '../models/form.model';
import { FormMetricsSummaryModel } from '../models/form-metrics.model';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type ActiveTab = 'manage' | 'analyze' | 'status';
type StatusFilter = FormStatus | 'all';

@Component({
  selector: 'app-forms-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MetricsBarComponent,
    FormsTableComponent,
    FormModalComponent,
    FormDetailModalComponent,
    AnalyticsDashboardComponent,
    HasPermissionDirective,
  ],
  templateUrl: './forms-list.component.html',
})
export class FormsListComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();

  readonly tabs: { id: ActiveTab; label: string }[] = [
    { id: 'manage', label: 'Administrar' },
    { id: 'analyze', label: 'Analizar' },
  ];

  readonly filterChips: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Todos' },
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'PUBLISHED', label: 'Publicado' },
    { value: 'PAUSED', label: 'Pausado' },
    { value: 'ARCHIVED', label: 'Archivado' },
  ];

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'forms-list-page-size';

  activeTab: ActiveTab = 'manage';
  currentFilter: StatusFilter = 'all';
  searchTerm = '';

  forms: FormListModel[] = [];
  metrics: FormMetricsSummaryModel | null = null;
  loadingForms = false;

  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  isModalOpen = false;
  savingForm = false;
  detailModalOpen = false;
  selectedForm: FormListModel | null = null;

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean { return this.globalPage > 0; }
  get hasNextPage(): boolean { return this.globalPage < this.globalTotalPages - 1; }
  get pageDisplay(): number  { return this.globalPage + 1; }

  constructor(
    private formsService: MarketingFormsService,
    private editorDrawer: FormEditorDrawerService,
    private templateService: TemplateService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadForms();

    this.search$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.globalPage = 0;
      this.loadForms();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Carga de datos ────────────────────────────────────────────────────────

  loadForms(): void {
    this.loadingForms = true;
    forkJoin({
      page: this.formsService.getForms({
        isTemplate: false,
        status: this.currentFilter !== 'all' ? this.currentFilter : undefined,
        searchName: this.searchTerm.trim() || undefined,
        page: this.globalPage,
        size: this.pageSize,
      }),
      published: this.formsService.getForms({ isTemplate: false, status: 'PUBLISHED', size: 1 }),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ page, published }) => {
        this.forms               = page.content ?? [];
        this.globalPage          = page.page    ?? this.globalPage;
        this.globalTotalPages    = page.totalPages    ?? 0;
        this.globalTotalElements = page.totalElements ?? 0;
        this.metrics = {
          totalForms:     this.globalTotalElements,
          publishedForms: published.totalElements ?? 0,
        };
        this.loadingForms = false;
      },
      error: () => {
        this.toast.error('No se pudieron cargar los formularios');
        this.loadingForms = false;
      },
    });
  }

  // ── Filtros, búsqueda y paginación ────────────────────────────────────────

  setTab(tab: ActiveTab): void {
    this.activeTab = tab;
  }

  setFilter(filter: StatusFilter): void {
    this.currentFilter = filter;
    this.globalPage    = 0;
    this.loadForms();
  }

  onSearchInput(): void {
    this.search$.next(this.searchTerm);
  }

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.loadForms();
  }

  changePageSize(size: number): void {
    this.pageSize   = size;
    this.globalPage = 0;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.loadForms();
  }

  // ── CRUD ──────────────────────────────────────────────────────────────────

  openCreateModal(): void {
    this.isModalOpen = true;
  }

  onCreateForm(payload: FormCreatePayload): void {
    this.savingForm = true;
    this.formsService.createForm(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: form => {
          this.savingForm  = false;
          this.isModalOpen = false;
          this.toast.created('Formulario');
          this.editorDrawer.open({ formId: form.id, formName: form.name });
          this.router.navigate(['/marketing-forms', form.id, 'editor']);
        },
        error: err => {
          this.savingForm = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo crear el formulario'));
        },
      });
  }

  onCreateFromTemplate(event: { templateId: number; name: string; internalNotes?: string }): void {
    this.savingForm = true;
    this.templateService.cloneTemplate(event.templateId, event.name, event.internalNotes)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: form => {
          this.savingForm  = false;
          this.isModalOpen = false;
          this.toast.created('Formulario');
          this.editorDrawer.open({ formId: form.id, formName: form.name });
          this.router.navigate(['/marketing-forms', form.id, 'editor']);
        },
        error: err => {
          this.savingForm = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo crear el formulario desde la plantilla'));
        },
      });
  }

  onDeleteForms(ids: number[]): void {
    if (!ids.length) return;
    forkJoin(ids.map(id => this.formsService.deleteForm(id)))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.toast.deleted('Formulario'); this.loadForms(); },
        error: (err: HttpErrorResponse) => {
          this.toast.error(extractApiErrorMessage(err, 'No se pudo eliminar el formulario'));
        },
      });
  }

  onDuplicateForm(id: number): void {
    this.formsService.duplicateForm(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.toast.success('Formulario duplicado'); this.loadForms(); },
        error: err => this.toast.error(extractApiErrorMessage(err, 'No se pudo duplicar el formulario')),
      });
  }

  // inc27: ciclo de vida coherente. Archivar un formulario publicado pide confirmación.
  async onStatusAction(event: { id: number; action: LifecycleAction }): Promise<void> {
    const { id, action } = event;
    const form = this.forms.find(f => f.id === id);

    if (action === 'archive' && form?.status === 'PUBLISHED') {
      const ok = await this.confirmDialog.confirmDanger({
        title: 'Archivar formulario',
        message: `El formulario "${form.name}" está publicado. Al archivarlo dejará de estar disponible y no recibirá más envíos. ¿Continuar?`,
        confirmText: 'Archivar',
      });
      if (!ok) return;
    }

    const config: Record<LifecycleAction, { call: Observable<unknown>; ok: string; errMsg: string }> = {
      publish: { call: this.formsService.publishForm(id), ok: 'Formulario publicado', errMsg: 'No se pudo publicar el formulario' },
      pause:   { call: this.formsService.pauseForm(id),   ok: 'Formulario pausado',   errMsg: 'No se pudo pausar el formulario' },
      resume:  { call: this.formsService.resumeForm(id),  ok: 'Formulario reanudado', errMsg: 'No se pudo reanudar el formulario' },
      archive: { call: this.formsService.archiveForm(id), ok: 'Formulario archivado', errMsg: 'No se pudo archivar el formulario' },
      restore: { call: this.formsService.restoreForm(id), ok: 'Formulario restaurado', errMsg: 'No se pudo restaurar el formulario' },
    };

    const { call, ok, errMsg } = config[action];
    call.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.toast.success(ok); this.loadForms(); },
      error: err => this.toast.error(extractApiErrorMessage(err, errMsg)),
    });
  }

  onOpenDetail(form: FormListModel): void {
    this.selectedForm    = form;
    this.detailModalOpen = true;
  }

  onCloseDetail(): void {
    this.detailModalOpen = false;
    this.selectedForm    = null;
  }

  onOpenEditor(formId: number): void {
    const form = this.forms.find(f => f.id === formId);
    this.editorDrawer.open({ formId, formName: form?.name });
    this.router.navigate(['/marketing-forms', formId, 'editor']);
  }

  onOpenSubmissions(formId: number): void {
    this.router.navigate(['/marketing-forms', formId, 'submissions']);
  }
}
